import * as Yup from 'yup'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { apenasDigitos, createValidation } from '_metronic/helpers'
import { identificadoresShape } from 'app/modules/apps/shared/documentos-pessoa/identificadores'

export const profissionalSchema = (
  t: TFunction,
  showEndereco: boolean,
  emEdicao: boolean,
  documentoEstrangeiroNaLista: boolean,
  cpfOriginal?: string,
  cnsOriginal?: string
) => {
  const v = createValidation(t)

  return Yup.object({
    empresas: Yup.array().when('funcionario.usuarioId', {
      is: (id: number | undefined) => id === undefined,
      then: (schema) => schema.min(1, v.requiredField('empresa', 'array')),
      otherwise: (schema) => schema.notRequired(),
    }),
    funcionario: Yup.object().shape({
      usuarioId: Yup.number().notRequired(),
      id: Yup.number().notRequired(),
      primeiroNome: Yup.string()
        .min(3, 'Minimo de 3 caracteres')
        .max(50, 'Máximo de 50 caracteres')
        .required('O campo nome é obrigatório')
        .test(
          'not-empty',
          'Campo não pode conter apenas espaços',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      sobreNome: Yup.string()
        .min(3, 'Minimo de 3 caracteres')
        .max(50, 'Máximo de 50 caracteres')
        .required('O campo sobrenome é obrigatório')
        .test(
          'not-empty',
          'Campo não pode conter apenas espaços',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      rqe: Yup.string().notRequired(),
      ...identificadoresShape(t, documentoEstrangeiroNaLista),
      // Compara só os dígitos: o input mascarado reformata o valor vindo da API
      // no mount (12345678901 -> 123.456.789-01) e a comparação literal acusaria
      // uma alteração que o usuário não fez.
      justificativa: Yup.string().when(['cpf', 'cns'], {
        is: (cpf?: string, cns?: string) =>
          emEdicao &&
          (apenasDigitos(cpf) !== apenasDigitos(cpfOriginal) ||
            apenasDigitos(cns) !== apenasDigitos(cnsOriginal)),
        then: (schema) =>
          schema.required('A justificativa é obrigatória quando o CPF/CNS é alterado'),
      }),

      crm: Yup.string()
        .trim()
        .min(2, 'Mínimo de 2 caracteres')
        .max(10, 'Máximo de 10 caracteres')
        .required('O campo registro no conselho é obrigatório'),

      email: Yup.string().email('Formato inválido').required('E-mail é obrigatório').nullable(),
      // Aceita vazio (campo opcional) e telefone fixo, que tem 10 dígitos contra
      // os 11 do celular.
      telefone: Yup.string()
        .notRequired()
        .nullable()
        .test('telefone-valido', 'Número Inválido', (value) => {
          if (!value) return true
          const digitos = value.replace(/\D/g, '').length
          return digitos === 10 || digitos === 11
        }),
      sexo: Yup.object().nullable().required('Selecione um gênero'),
      nascimento: Yup.string().required('O campo data de nascimento é obrigatório'),

      especialidades: Yup.array().when('usuarioId', {
        is: (id: number | undefined) => id === undefined,
        then: (schema) => schema.min(1, 'Você deve selecionar pelo menos uma especialidade'),
        otherwise: (schema) => schema.notRequired(),
      }),

      uf: Yup.object().nullable().required('Selecione um estado'),
      vinculo: Yup.object().nullable().notRequired(),
      conselho: Yup.object().nullable().required('Conselho é obrigatório'),
      raca: Yup.object().nullable().notRequired(),
      genero: Yup.object().nullable().notRequired(),
      nacionalidade: Yup.object().nullable().notRequired(),
    }),
    endereco: Yup.object().shape({
      // O `matches` precisa ficar dentro do `when`: fora dele a string vazia de
      // um profissional sem endereço reprova o formulário inteiro, e o campo nem
      // está renderizado para mostrar o erro.
      cep: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .required('O campo CEP é obrigatório')
            .matches(/^\d{2}\.\d{3}-\d{3}$/, 'Formato deve ser 00.000-000'),
        otherwise: (schema) => schema.notRequired(),
      }),
      logradouro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(5, 'Minimo de 5 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo logradouro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      numero: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) => schema.required('O campo número é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      complemento: Yup.string().notRequired(),
      bairro: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo bairro é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      cidade: Yup.string().when([], {
        is: () => showEndereco,
        then: (schema) =>
          schema
            .min(3, 'Minimo de 3 caracteres')
            .max(50, 'Máximo de 50 caracteres')
            .required('O campo cidade é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
      uf: Yup.object()
        .nullable()
        .when([], {
          is: () => showEndereco,
          then: (schema) => schema.required('O campo UF é obrigatório'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
  })
}
